import type { SessionInfo } from '@cognitoforms/api/services/session';
import type { TreeMode } from 'src/components/navigation/FormTree.vue';
import type { Ref } from 'vue';
import { getDeviceType } from '@cognitoforms/utils/user-agent';
import { computed, reactive, watch } from 'vue';
import type { Route } from 'vue-router';
import { globalStore } from 'src/stores/global-store';

export type NavStatus = 'docked' | 'popper' | 'closed';
export type TreeModeWithExpansion = Exclude<TreeMode, 'task' | 'pinned' | 'viewTabs'>;
export type TreeModeWithRenaming = Exclude<TreeMode, 'task' | 'viewTabs'>;

export interface INavState {
	navWidth: number;
	persistedNavWidth: number;
	isOpen: boolean;
	isEntryDetailsOpen: boolean,
	entryDetailsWidth: number,
	entryDetailsWidthPersisted: number,
	entryDetailsSnapped: boolean,
	isPopper: boolean;
	showOverlay: boolean;
	isSupportOpen: boolean;
	isSupportPinned: boolean;
	canPinSupport: boolean;
	supportWidth: number;
	supportWidthPersisted: number;
	isHandlingInteraction: boolean;
	expandedNodes: Record<TreeModeWithExpansion, string[]>;
	renamingState: Record<TreeModeWithRenaming, string>;
	innerWidth: number;
}

const extraSpaceForSafeArea = getDeviceType(navigator.userAgent) === 'iphone' ? 45 : 0;
const defaultNavWidth = 420 + extraSpaceForSafeArea;
const minimumNavWidth = 200 + extraSpaceForSafeArea;
const maxNavWidthPercent = 0.7;
// navGap is the minimum width between the nav and details when dragging details, after sidebar and grid have both hit their min widths
const navGap = 10;

const defaultEntryDetailsWidth = 500;
const minimumEntryDetailsWidth = 365;
const minimumWidthBeforeSnap = 50;

const minimumGridWidth = 420;

const minimumSupportWidth = 320;
const maxSupportWidthPercent = 0.40;
const minimumScreenWidthToPin = 1023; // $b10

let disableShowPopper = false;
let mouseListener: ((e: MouseEvent) => void) | null = null;

// Prevent popper from reopening immediately after closing until mouse leaves 72px top-left area
const startMouseListener = () => {
	if (mouseListener)
		return;

	disableShowPopper = true;

	mouseListener = (e: MouseEvent) => {
		const { clientX, clientY } = e;
		const withinSquare = clientX <= 72 && clientY <= 72;

		if (!withinSquare) {
			disableShowPopper = false;

			if (mouseListener) {
				window.removeEventListener('mousemove', mouseListener);
				mouseListener = null;
			}
		}
	};

	window.addEventListener('mousemove', mouseListener);
};

// https://en.wikipedia.org/wiki/Clamp_(function)
const clamp = (num: number, min: number, max: number) => {
	return Math.max(Math.min(num, max), min);
};

const getValidNavWidth = (width: number, isEntryDetailsOpen: boolean, isSupportOpen: boolean, innerWidth: number) => {
	return clamp(width, minimumNavWidth, Math.min(innerWidth * maxNavWidthPercent, innerWidth - minimumGridWidth - (isEntryDetailsOpen ? minimumEntryDetailsWidth : 0) - (isSupportOpen ? minimumSupportWidth : 0)));
};

const getValidEntryDetailsWidth = (width: number, innerWidth: number) => {
	return clamp(width, minimumEntryDetailsWidth, innerWidth);
};

const getValidSupportWidth = (width: number, innerWidth: number) => {
	return clamp(width, minimumSupportWidth, Math.min(innerWidth * maxSupportWidthPercent, innerWidth));
};

const parseNumber = (value: string, defaultValue: number) => (
	value !== null && !isNaN(Number(value)) ? parseInt(value, 10) : defaultValue
);

const parseBool = (value: string, defaultValue: boolean) => (
	value !== null ? value === 'true' : defaultValue
);

const isIterable = (obj: unknown) => (obj) ? typeof obj[Symbol.iterator] === 'function' : false;

const parseList = (value: string, defaultValue: string[]) => {
	if (value === null)
		return defaultValue;

	try {
		const parsed = JSON.parse(value);
		return (isIterable(parsed)) ? parsed : defaultValue;
	}
	catch (e) {
		return defaultValue;
	}
};

let orgSpecificPostfix: string;

const setOrgSpeficicPostfix = (value: string) => {
	orgSpecificPostfix = value;
};

// For mobile we need to determine if the sidebar should be open or if the page should be
const handleMobileMenuState = (isOpen: boolean, route: Route, isMobile: boolean) => {
	if (route.meta.showNavAsPopperOnly)
		return false;
	if (!isMobile && !isOpen && localStorage.getItem(`isOpen_${orgSpecificPostfix}`) !== 'false')
		return true;
	else if (isMobile) {
		if (isOpen && route.meta.mobileMenu !== true)
			return false;
		else if (!isOpen && route.meta.mobileMenu === true)
			return true;
	}

	return isOpen;
};

export const useNavState = (session: SessionInfo, route: Route, isMobile: Ref<boolean>) => {
	setOrgSpeficicPostfix(`${session.organizationId}_${session.userId}`);

	// JTODO The below can be removed eventually, just here for the time were are transferring these values over from being user specific to org specific
	const initializeValue = (valueName: string) => {
		const userSpecificPostfix = `${session.userId}`;
		if (localStorage.getItem(`${valueName}_${userSpecificPostfix}`) && !localStorage.getItem(`${valueName}_${orgSpecificPostfix}`)) {
			localStorage.setItem(`${valueName}_${orgSpecificPostfix}`, localStorage.getItem(`${valueName}_${userSpecificPostfix}`));
			if (globalStore.user?.Organizations?.length === 1)
				localStorage.removeItem(`${valueName}_${userSpecificPostfix}`);
		}
	};

	initializeValue('navWidth');
	initializeValue('isOpen');
	initializeValue('entryDetailsWidth');

	const initialWidth = window.innerWidth;

	// Nav State
	const storedNavWidth = parseNumber(localStorage.getItem(`navWidth_${orgSpecificPostfix}`), defaultNavWidth);
	const navWidth = getValidNavWidth(storedNavWidth, false, false, initialWidth);

	const getIsOpenFromLocalStorage = () => {
		const storedIsOpenState = localStorage.getItem(`isOpen_${orgSpecificPostfix}`);
		return parseBool(storedIsOpenState, true) && !route.meta.showNavAsPopperOnly;
	};
	const isOpen = getIsOpenFromLocalStorage();

	const storedDefaultExpandedState = localStorage.getItem(`expandedNodes_default_${orgSpecificPostfix}`);

	// Entry Details State
	const storedEntryDetailsWidthPercentage = localStorage.getItem(`entryDetailsWidth_${orgSpecificPostfix}`);
	const storedEntryDetailsWidthPixels = storedEntryDetailsWidthPercentage ? +storedEntryDetailsWidthPercentage * (initialWidth - navWidth) : defaultEntryDetailsWidth;
	const entryDetailsWidth = getValidEntryDetailsWidth(storedEntryDetailsWidthPixels, initialWidth);
	const entryDetailsSnapped = false;

	// Support State
	const storedSupportWidth = parseNumber(localStorage.getItem(`supportWidth_${orgSpecificPostfix}`), defaultNavWidth);
	const supportWidth = getValidSupportWidth(storedSupportWidth, initialWidth);

	const state = reactive<INavState>({
		navWidth,
		persistedNavWidth: navWidth,
		isOpen: route.meta.showNavAsPopperOnly ? false : isMobile.value ? handleMobileMenuState(isOpen, route, isMobile.value) : isOpen,
		isEntryDetailsOpen: false,
		entryDetailsWidth,
		entryDetailsWidthPersisted: entryDetailsWidth,
		entryDetailsSnapped,
		isPopper: false,
		showOverlay: false,
		isSupportOpen: false,
		isSupportPinned: false,
		canPinSupport: initialWidth >= minimumScreenWidthToPin,
		supportWidth,
		supportWidthPersisted: supportWidth,
		isHandlingInteraction: false,
		innerWidth: initialWidth,
		expandedNodes: {
			default: parseList(storedDefaultExpandedState, []),
			archived: []
		},
		renamingState: {
			default: null,
			archived: null,
			pinned: null
		}
	});

	const getRightSidebarWidth = () => (state.isSupportPinned ? state.supportWidth : 0) + (state.isEntryDetailsOpen ? state.entryDetailsWidth : 0);
	const getRightSidebarPersistedWidth = () => (state.isSupportPinned ? state.supportWidthPersisted : 0) + (state.isEntryDetailsOpen ? state.entryDetailsWidthPersisted : 0);

	// Calculate the ratio of the support sidebar width to the combined width of both right-side panels (entry details + support)
	const getSupportSidebarRatio = (usePersistedValues = false) => {
		if (!state.isSupportPinned)
			return 0;

		const supportWidth = (usePersistedValues) ? state.supportWidthPersisted : state.supportWidth;
		const totalWidth = (usePersistedValues) ? getRightSidebarPersistedWidth() : getRightSidebarWidth();
		return supportWidth / totalWidth;
	};

	// Set Nav Width
	const setWidth = (width: number, isPersisted: boolean) => {
		const newWidth = getValidNavWidth(width, state.isEntryDetailsOpen, state.isSupportPinned, state.innerWidth);
		state.navWidth = newWidth;

		// Resize the right sidebars when dragging nav and there's not enough room. If the new width is larger than the min, set it
		const isAnyRightSidebarOpen = state.isEntryDetailsOpen || state.isSupportPinned;
		if (isAnyRightSidebarOpen && state.innerWidth < state.navWidth + getRightSidebarWidth() + minimumGridWidth) {
			const newRightSidebarWidth = state.innerWidth - state.navWidth - minimumGridWidth;
			const supportSidebarRatio = getSupportSidebarRatio();

			state.entryDetailsWidth = Math.max(newRightSidebarWidth * (1 - supportSidebarRatio), minimumEntryDetailsWidth);
			state.supportWidth = Math.max(newRightSidebarWidth * supportSidebarRatio, minimumSupportWidth);
		}

		if (isPersisted) {
			state.persistedNavWidth = newWidth;
			localStorage.setItem(`navWidth_${orgSpecificPostfix}`, newWidth.toString());
		}
	};

	const setEntryDetailsWidth = (entryDetailsWidth: number, isPersisted: boolean) => {
		const navWidth = state.isOpen ? state.navWidth : 0;
		const supportWidth = state.isSupportPinned ? state.supportWidth : 0;
		const snapped = state.innerWidth - minimumWidthBeforeSnap - supportWidth <= entryDetailsWidth;

		// Don't allow it within 10 pixels of the sidebar; allow it into the 10px gap so that it can push the sidebar
		entryDetailsWidth = Math.min(entryDetailsWidth, state.innerWidth - navWidth - supportWidth - navGap);

		if (state.isEntryDetailsOpen && snapped) {
			state.entryDetailsWidth = state.innerWidth - supportWidth;
			state.entryDetailsSnapped = true;
		}
		else {
			requestAnimationFrame(()=>{
				if (state.isEntryDetailsOpen && state.entryDetailsWidth < state.innerWidth - (navWidth - minimumGridWidth)) {
					const newEntryDetailsWidth = getValidEntryDetailsWidth(entryDetailsWidth, state.innerWidth);
					state.entryDetailsWidth = Math.max(newEntryDetailsWidth, minimumEntryDetailsWidth);
				}
				state.entryDetailsSnapped = false;
			});
		}

		// Shrink the nav and keep grid at 380px until nav is at its min width
		if (state.isOpen && state.isEntryDetailsOpen && state.navWidth + getRightSidebarWidth() + minimumGridWidth > state.innerWidth) {
			const newNavWidth = state.innerWidth - getRightSidebarWidth() - minimumGridWidth;
			state.navWidth = Math.max(newNavWidth, minimumNavWidth);
		}

		// Shrink the nav if there's not enough room when opening entry details
		if (state.isOpen && (state.navWidth + getRightSidebarWidth() + navGap > state.innerWidth)) {
			const newNavWidth = state.innerWidth - getRightSidebarWidth() - navGap;
			state.navWidth = Math.max(newNavWidth, minimumNavWidth);
		}

		if (isPersisted) {
			state.entryDetailsWidthPersisted = state.entryDetailsWidth;
			// Save entry details width as a percentage of available space
			localStorage.setItem(`entryDetailsWidth_${orgSpecificPostfix}`, (state.entryDetailsWidth / (state.innerWidth - navWidth)).toString());
			localStorage.setItem(`navWidth_${orgSpecificPostfix}`, state.navWidth.toString());
		}
	};

	const setEntryDetailsIsOpen = (isOpen: boolean) => {
		state.isEntryDetailsOpen = isOpen;
		if (!isOpen)
			return;

		let newEntryDetailsWidth = state.entryDetailsWidth;

		const isOtherSidebarOpen = state.isOpen || state.isSupportPinned;
		if (isOtherSidebarOpen && state.isEntryDetailsOpen && (getRightSidebarWidth() > state.innerWidth - state.navWidth - navGap)) {
			const navWidth = state.isOpen ? state.navWidth : 0;
			const supportWidth = state.isSupportPinned ? state.supportWidth : 0;

			newEntryDetailsWidth = state.innerWidth - navWidth - supportWidth - navGap;
		}

		// Take space from nav and support if the new width is less than minimumEntryDetailsWidth
		const pixelsNeedeed = minimumEntryDetailsWidth - newEntryDetailsWidth;
		if (pixelsNeedeed > 0 && state.isOpen && state.isSupportPinned) {
			const pixelsUntilNavMinWidth = state.navWidth - minimumNavWidth;
			const pixelsUntilSupportMinWidth = state.supportWidth - minimumSupportWidth;

			// Take a ratio of available pixels from nav and entry details
			const totalAvailablePixels = pixelsUntilNavMinWidth + pixelsUntilSupportMinWidth;
			const navPixelsToTake = Math.round(pixelsNeedeed * (pixelsUntilNavMinWidth / totalAvailablePixels));
			const supportPixelsToTake = pixelsNeedeed - navPixelsToTake;

			state.navWidth = Math.max(state.navWidth - navPixelsToTake, minimumNavWidth);
			state.supportWidth = Math.max(state.supportWidth - supportPixelsToTake, minimumSupportWidth);
			newEntryDetailsWidth = minimumEntryDetailsWidth;
		}

		state.entryDetailsWidth = newEntryDetailsWidth;
	};

	// Open State
	const setIsOpen = (isOpen: boolean, isPersisted: boolean = true) => {
		state.isOpen = isOpen;
		state.showOverlay = false;

		if (!isOpen)
			startMouseListener();

		if (isOpen)
			state.isPopper = false;

		// Open nav in space available. Adjust details width if needed
		const isAnyRightSidebarOpen = state.isEntryDetailsOpen || state.isSupportPinned;
		if (state.isOpen && isAnyRightSidebarOpen && (state.innerWidth < getRightSidebarWidth() + state.navWidth + navGap)) {
			state.navWidth = minimumNavWidth;
			if (state.innerWidth < getRightSidebarWidth() + state.navWidth + navGap) {
				const newRightSidebarWidth = state.innerWidth - state.navWidth - minimumGridWidth;
				const supportSidebarRatio = getSupportSidebarRatio();

				state.entryDetailsWidth = Math.max(newRightSidebarWidth * (1 - supportSidebarRatio), minimumEntryDetailsWidth);
				state.supportWidth = Math.max(newRightSidebarWidth * supportSidebarRatio, minimumSupportWidth);
			}
		}

		if (isPersisted)
			localStorage.setItem(`isOpen_${orgSpecificPostfix}`, isOpen.toString());
	};

	// Support Pinned Logic
	const handleSupportPinned = () => {
		let newSupportWidth = state.supportWidth;

		const isAnyOtherSidebarOpen = state.isOpen || state.isEntryDetailsOpen;
		if (isAnyOtherSidebarOpen && (getRightSidebarWidth() > state.innerWidth - state.navWidth - navGap)) {
			const navWidth = state.isOpen ? state.navWidth : 0;
			const entryDetailsWidth = state.isEntryDetailsOpen ? state.entryDetailsWidth : 0;

			newSupportWidth = state.innerWidth - navWidth - entryDetailsWidth - navGap;
		}

		// Take space from nav and entry details if the new width is less than minimumSupportWidth
		const pixelsNeedeed = minimumSupportWidth - newSupportWidth;
		if (pixelsNeedeed > 0 && state.isOpen && state.isEntryDetailsOpen) {
			const pixelsUntilNavMin = state.navWidth - minimumNavWidth;
			const pixelsUntilEntryDetailsMin = state.entryDetailsWidth - minimumEntryDetailsWidth;

			// Take a ratio of available pixels from nav and entry details
			const totalAvailablePixels = pixelsUntilNavMin + pixelsUntilEntryDetailsMin;
			const navPixelsToTake = Math.round(pixelsNeedeed * (pixelsUntilNavMin / totalAvailablePixels));
			const entryDetailsPixelsToTake = pixelsNeedeed - navPixelsToTake;

			state.navWidth = Math.max(state.navWidth - navPixelsToTake, minimumNavWidth);
			state.entryDetailsWidth = Math.max(state.entryDetailsWidth - entryDetailsPixelsToTake, minimumEntryDetailsWidth);
			newSupportWidth = minimumSupportWidth;
		}

		state.supportWidth = newSupportWidth;
	};

	const setSupportIsOpen = (isOpen: boolean) => {
		state.isSupportOpen = isOpen;

		if (isMobile.value)
			state.isSupportPinned = true;

		// Run resize logic if support is being opened and is pinned
		if (isOpen && state.isSupportPinned)
			handleSupportPinned();
	};

	const setSupportIsPinned = (isPinned: boolean) => {
		state.isSupportPinned = isPinned;
		if (isPinned)
			handleSupportPinned();
	};

	const setSupportWidth = (supportWidth: number, isPersisted: boolean) => {
		const navWidth = state.isOpen ? state.navWidth : 0;
		const entryDetailsWidth = state.isEntryDetailsOpen ? state.entryDetailsWidth : 0;

		supportWidth = Math.min(supportWidth, state.innerWidth - navWidth - entryDetailsWidth - navGap);

		requestAnimationFrame(()=>{
			if (state.supportWidth < state.innerWidth - (navWidth - minimumGridWidth)) {
				const newSupportWidth = getValidSupportWidth(supportWidth, state.innerWidth);
				state.supportWidth = Math.max(newSupportWidth, minimumSupportWidth);
			}
		});

		// Shrink the nav if there's not enough room when opening entry details
		if (state.isOpen && (state.navWidth + getRightSidebarWidth() + minimumGridWidth > state.innerWidth)) {
			const newNavWidth = state.innerWidth - getRightSidebarWidth() - minimumGridWidth;
			state.navWidth = Math.max(newNavWidth, minimumNavWidth);
		}

		if (isPersisted) {
			state.supportWidthPersisted = state.supportWidth;
			localStorage.setItem(`supportWidth_${orgSpecificPostfix}`, state.supportWidthPersisted.toString());
			localStorage.setItem(`navWidth_${orgSpecificPostfix}`, state.navWidth.toString());
		}
	};

	const handleWindowResize = (innerWidth: number) => {
		// If you resize vertically, do nothing
		if (window.innerWidth === state.innerWidth)
			return;

		const isAnyRightSidebarOpen = state.isEntryDetailsOpen || state.isSupportPinned;
		if (state.isOpen && !isAnyRightSidebarOpen // If only the navbar is open
			&& window.innerWidth < state.innerWidth // and we are shrinking the window
			&& (window.innerWidth - state.navWidth) < (800 - defaultNavWidth)) { // and the homepage content is less than 800 - default nav width pixels
			const currentNavWidthPercentage = Number(state.navWidth / state.innerWidth).toFixed(2);
			const newNavWidth = Number(currentNavWidthPercentage) * window.innerWidth;

			if (newNavWidth > minimumNavWidth) {
				// Do not let homepage be less than 420px
				if (window.innerWidth - newNavWidth > 420) {
					state.navWidth = Math.round(newNavWidth);
				}
				else {
					state.navWidth = window.innerWidth - 420;
				}
				// Saving to local storage so your nav width persists on refresh
				localStorage.setItem(`navWidth_${orgSpecificPostfix}`, state.navWidth.toString());
			}
		}

		// Users no longer able to pin/unpin support on small widths
		state.canPinSupport = innerWidth >= minimumScreenWidthToPin;

		// Force support to be unpinned on small widths unless on mobile
		if (isMobile.value)
			state.isSupportPinned = true;
		else if (innerWidth < minimumScreenWidthToPin)
			state.isSupportPinned = false;

		// Moved this down so i could compare previous and current window widths in handleWindowResize
		state.innerWidth = innerWidth;

		// If nav and either right sidebar are open, shrink them
		if (state.isOpen && isAnyRightSidebarOpen) {
			const newNavWidth = state.innerWidth - (getRightSidebarPersistedWidth() + minimumGridWidth);
			state.navWidth = Math.min(state.persistedNavWidth, Math.max(newNavWidth, minimumNavWidth));

			const supportSidebarRatio = getSupportSidebarRatio(true);
			const newRightSidebarWidth = Math.min(getRightSidebarPersistedWidth(), state.innerWidth - state.navWidth - minimumGridWidth);

			state.entryDetailsWidth = Math.max(newRightSidebarWidth * (1 - supportSidebarRatio), minimumEntryDetailsWidth);
			state.supportWidth = Math.max(newRightSidebarWidth * supportSidebarRatio, minimumSupportWidth);
		}

		// If only details is open, grid shrinks to 380, then details begins to overlap until it snaps
		const snapped = state.entryDetailsWidth >= state.innerWidth - minimumWidthBeforeSnap;
		if (!state.isOpen && state.isEntryDetailsOpen && snapped) {
			state.entryDetailsWidth = state.innerWidth;
			state.entryDetailsSnapped = true;
		}
		else {
			state.entryDetailsSnapped = false;
		}
	};

	// Watch for showNavAsPopper changes
	watch(() => route.meta.showNavAsPopperOnly, (showNavAsPopperOnly) => {
		if (showNavAsPopperOnly)
			setIsOpen(false, false);
		else
			setIsOpen(getIsOpenFromLocalStorage(), true);
	});

	// Is Popper State
	const setIsPopper = (newState: boolean, showOverlay: boolean = false) => {
		const popperIsLocked = (!newState && state.isPopper && state.isHandlingInteraction);
		if (state.isOpen || popperIsLocked || (newState && disableShowPopper))
			return;

		state.isPopper = newState;
		state.showOverlay = newState && showOverlay;
	};

	watch(() => route.fullPath, () => {
		setIsPopper(false);
		if (isMobile.value) {
			const newIsOpen = handleMobileMenuState(isOpen, route, isMobile.value);
			setIsOpen(newIsOpen, false);
		}
	});

	watch(isMobile, (newVal) => {
		const newIsOpen = handleMobileMenuState(state.isOpen, route, newVal);
		if (!newIsOpen)
			setIsPopper(false);
		setIsOpen(newIsOpen, false);
	});

	// Is Handling Interaction State
	const setIsHandlingInteraction = (isInteracting: boolean) => {
		state.isHandlingInteraction = isInteracting;
	};

	// Set isHandlingInteraction to false when navigating to a new route
	watch(() => route.fullPath, () => {
		state.isHandlingInteraction = false;
		setIsPopper(false);
	});

	// Expanded Nodes State
	const setTreeExpansionState = (treeMode: TreeModeWithExpansion, expansionState: string[]) => {
		state.expandedNodes[treeMode] = expansionState;
		if (treeMode === 'default')
			localStorage.setItem(`expandedNodes_${treeMode}_${orgSpecificPostfix}`, JSON.stringify(expansionState));
	};

	const setNodeExpandState = (treeMode: TreeModeWithExpansion, nodeId: string, isExpanded: boolean) => {
		const expandedNodes = state.expandedNodes[treeMode];
		if (isExpanded) {
			if (!expandedNodes.includes(nodeId))
				expandedNodes.push(nodeId);
		}
		else
			expandedNodes.splice(expandedNodes.indexOf(nodeId), 1);

		setTreeExpansionState(treeMode, expandedNodes);
	};

	// Renaming Node State
	const isRenaming = computed(() =>
		!!state.renamingState.default ||
		!!state.renamingState.archived ||
		!!state.renamingState.pinned
	);

	const setNodeRenameState = (nodeId: string, targetTree: TreeModeWithRenaming) => {
		state.renamingState[targetTree] = nodeId;
	};

	const clearNodeRenameState = () => {
		for (const treeMode in state.renamingState) {
			state.renamingState[treeMode] = null;
		}
	};

	return {
		navWidth: computed(() => state.navWidth),
		isOpen: computed(() => state.isOpen),
		isEntryDetailsOpen: computed(() => state.isEntryDetailsOpen),
		entryDetailsWidth: computed(() => state.entryDetailsWidth),
		entryDetailsSnapped: computed(() => state.entryDetailsSnapped),
		isPopper: computed(() => state.isPopper),
		showOverlay: computed(() => state.showOverlay),
		isSupportOpen: computed(() => state.isSupportOpen),
		isSupportPinned: computed(() => state.isSupportPinned),
		canPinSupport: computed(() => state.canPinSupport),
		supportWidth: computed(() => state.supportWidth),
		isHandlingInteraction: computed(() => state.isHandlingInteraction || isRenaming.value),
		expandedNodes: state.expandedNodes,
		renameState: state.renamingState,
		handleWindowResize,
		setEntryDetailsWidth,
		setEntryDetailsIsOpen,
		setWidth,
		setIsOpen,
		setIsPopper,
		setSupportIsOpen,
		setSupportIsPinned,
		setSupportWidth,
		setIsHandlingInteraction,
		setTreeExpansionState,
		setNodeExpandState,
		setNodeRenameState,
		clearNodeRenameState
	};
};

export type NavState = ReturnType<typeof useNavState>;